import { useState, useCallback, useEffect } from 'react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { AircraftTypeForm } from './AircraftTypeForm'
import {
  useAircraftType,
  useCreateAircraftType,
  useUpdateAircraftType,
} from '@/hooks/useAircraftTypes'
import type { AircraftTypeFormValues } from './aircraft-type-form-schema'
import { checkAircraftTypeExists } from '@/lib/api/aircraft-types'
import { toast } from 'sonner'

interface AircraftTypeSidePanelProps {
  /** Whether the panel is open */
  open: boolean
  /** Called when open state changes */
  onOpenChange: (open: boolean) => void
  /** Aircraft type ID for edit mode, undefined for create mode */
  aircraftTypeId?: string
}

/**
 * Side panel for aircraft type create/edit
 * Slides in from the right side
 * Includes dirty state detection and close confirmation
 */
export function AircraftTypeSidePanel({
  open,
  onOpenChange,
  aircraftTypeId,
}: AircraftTypeSidePanelProps) {
  const isEdit = !!aircraftTypeId

  // Form dirty state
  const [isDirty, setIsDirty] = useState(false)
  const [showExitConfirm, setShowExitConfirm] = useState(false)
  const [pendingClose, setPendingClose] = useState(false)

  // Reset dirty state when panel opens
  // This is intentional - we want to reset state when parent opens the panel
  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsDirty(false)
    }
  }, [open])

  // Fetch existing aircraft type for edit mode
  const { data: aircraftType, isLoading: isLoadingAircraftType } = useAircraftType(
    aircraftTypeId
  )

  // Mutations
  const createMutation = useCreateAircraftType()
  const updateMutation = useUpdateAircraftType()

  const isLoading =
    isLoadingAircraftType ||
    createMutation.isPending ||
    updateMutation.isPending

  // Handle panel open change with dirty state check
  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen && isDirty) {
        // User is trying to close but has unsaved changes
        setPendingClose(true)
        setShowExitConfirm(true)
        return
      }
      // Clean close
      onOpenChange(newOpen)
      if (!newOpen) {
        // Reset state when closing
        setIsDirty(false)
      }
    },
    [isDirty, onOpenChange]
  )

  // Handle confirmed close (discard changes)
  const handleConfirmClose = useCallback(() => {
    setShowExitConfirm(false)
    setIsDirty(false)
    if (pendingClose) {
      setPendingClose(false)
      onOpenChange(false)
    }
  }, [pendingClose, onOpenChange])

  // Handle cancel close (continue editing)
  const handleCancelClose = useCallback(() => {
    setShowExitConfirm(false)
    setPendingClose(false)
  }, [])

  // Handle form submission
  const handleSubmit = async (data: AircraftTypeFormValues) => {
    try {
      if (isEdit && aircraftTypeId) {
        // Update existing aircraft type
        await updateMutation.mutateAsync({ id: aircraftTypeId, data })
      } else {
        // Check for duplicates before creating
        const existing = await checkAircraftTypeExists(
          data.manufacturer,
          data.model,
          data.variant || undefined
        )

        if (existing) {
          toast.error('Aircraft type already exists', {
            description: `${existing.manufacturer} ${existing.model}${existing.variant ? ` ${existing.variant}` : ''} is already in the database.`,
          })
          return
        }

        // Create new aircraft type
        await createMutation.mutateAsync(data)
      }

      // Success - close panel
      setIsDirty(false)
      onOpenChange(false)
    } catch (error) {
      console.error('Error saving aircraft type:', error)
    }
  }

  // Handle cancel button
  const handleCancel = useCallback(() => {
    handleOpenChange(false)
  }, [handleOpenChange])

  // Track dirty state from form changes
  // Note: We could add onDirtyChange to AircraftTypeForm for more precise tracking
  // For now, we'll consider any submit attempt as a change indicator
  const handleFormChange = useCallback(() => {
    if (!isDirty) {
      setIsDirty(true)
    }
  }, [isDirty])

  return (
    <>
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-[700px] overflow-y-auto"
          onInteractOutside={(e) => {
            if (isDirty) {
              e.preventDefault()
              setShowExitConfirm(true)
            }
          }}
          onEscapeKeyDown={(e) => {
            if (isDirty) {
              e.preventDefault()
              setShowExitConfirm(true)
            }
          }}
        >
          <SheetHeader>
            <SheetTitle>
              {isEdit
                ? `Edit Aircraft Type: ${aircraftType?.manufacturer ?? ''} ${aircraftType?.model ?? '...'}`
                : 'Add New Aircraft Type'}
            </SheetTitle>
            <SheetDescription>
              {isEdit
                ? 'Update the aircraft type specifications below.'
                : 'Add a new aircraft type to the global reference library. Search the database to auto-fill specifications.'}
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6" onChange={handleFormChange}>
            {isLoadingAircraftType && isEdit ? (
              <div className="py-8 text-center text-muted-foreground">
                Loading aircraft type details...
              </div>
            ) : (
              <AircraftTypeForm
                key={aircraftTypeId ?? 'create'}
                aircraftType={aircraftType}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
                isLoading={isLoading}
                showApiSearch={!isEdit}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Exit Confirmation Dialog */}
      <AlertDialog open={showExitConfirm} onOpenChange={setShowExitConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to close? Your
              changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelClose}>
              Continue editing
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmClose}>
              Discard changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
